import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import './App.css';

const COLORS = ['#1A1A1A', '#E8A020', '#2E7D32', '#C62828', '#5C8CFF'];

export default function App() {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setChecking(false);
    });
    supabase.auth.onAuthStateChange((_e, session) => setSession(session));
  }, []);

  if (checking) return <div style={styles.center}><div style={styles.spinner} /></div>;
  if (!session) return <LoginScreen />;

  return (
    <div style={styles.app}>
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} session={session} />
      <main style={styles.main}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'verifications' && <VerificationsTab />}
        {activeTab === 'bookings' && <BookingsTab />}
        {activeTab === 'disputes' && <DisputesTab />}
        {activeTab === 'providers' && <ProvidersTab />}
        {activeTab === 'customers' && <CustomersTab />}
        {activeTab === 'revenue' && <RevenueTab />}
      </main>
    </div>
  );
}

function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const { data: userData } = await supabase
        .from('users').select('is_admin').eq('id', data.user.id).single();
      if (!userData?.is_admin) {
        await supabase.auth.signOut();
        setError('Access denied. Admin accounts only.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginCard}>
        <h1 style={styles.loginTitle}>🎉 Utsav Admin</h1>
        <p style={styles.loginSub}>Sign in to manage your platform</p>
        <form onSubmit={handleLogin}>
          <input style={styles.loginInput} type="email" placeholder="Admin email"
            value={email} onChange={e => setEmail(e.target.value)} required />
          <input style={styles.loginInput} type="password" placeholder="Password"
            value={password} onChange={e => setPassword(e.target.value)} required />
          {error && <p style={styles.loginError}>{error}</p>}
          <button style={styles.loginBtn} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in →'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Sidebar({ activeTab, setActiveTab, session }) {
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '◈' },
    { id: 'verifications', label: 'Verifications', icon: '✓' },
    { id: 'bookings', label: 'Bookings', icon: '📅' },
    { id: 'disputes', label: 'Disputes', icon: '⚠️' },
    { id: 'providers', label: 'Providers', icon: '🛠' },
    { id: 'customers', label: 'Customers', icon: '👥' },
    { id: 'revenue', label: 'Revenue', icon: '₹' },
  ];
  return (
    <aside style={styles.sidebar}>
      <div style={styles.sidebarTop}>
        <div style={styles.sidebarLogoRow}>
          <img src="/logo-mark-64.png" alt="" style={styles.sidebarLogoImg} />
          <h2 style={styles.sidebarTitle}>Utsav</h2>
        </div>
        <p style={styles.sidebarSub}>Admin dashboard</p>
      </div>
      <nav>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.navItem, ...(activeTab === tab.id ? styles.navItemActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={styles.navIcon}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </nav>
      <div style={styles.sidebarBottom}>
        <p style={styles.sidebarEmail}>{session?.user?.email}</p>
        <button style={styles.logoutBtn} onClick={() => supabase.auth.signOut()}>
          Log out
        </button>
      </div>
    </aside>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState({
    totalBookings: 0, totalRevenue: 0, totalProviders: 0,
    totalCustomers: 0, pendingVerifications: 0, todayBookings: 0,
  });
  const [revenueData, setRevenueData] = useState([]);
  const [bookingsByCity, setBookingsByCity] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOverview(); }, []);

  async function fetchOverview() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [
        { count: bookings },
        { data: revenue },
        { count: providers },
        { count: customers },
        { count: pending },
        { count: todayCount },
        { data: recent },
      ] = await Promise.all([
        supabase.from('bookings').select('*', { count: 'exact', head: true }),
        supabase.from('bookings').select('commission_amount').eq('status', 'confirmed'),
        supabase.from('providers').select('*', { count: 'exact', head: true }),
        supabase.from('users').select('*', { count: 'exact', head: true }).eq('role', 'customer'),
        supabase.from('verification_requests').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('bookings').select('*', { count: 'exact', head: true }).gte('created_at', today),
        supabase.from('bookings').select('*, users!customer_id(name)').order('created_at', { ascending: false }).limit(5),
      ]);

      const totalRevenue = (revenue || []).reduce((s, b) => s + (b.commission_amount || 0), 0);
      setStats({ totalBookings: bookings, totalRevenue, totalProviders: providers, totalCustomers: customers, pendingVerifications: pending, todayBookings: todayCount });
      setRecentBookings(recent || []);

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const { data: allBookings } = await supabase.from('bookings').select('created_at, commission_amount, event_type, venue').eq('status', 'confirmed');
      const monthlyData = months.map((month, i) => {
        const monthBookings = (allBookings || []).filter(b => new Date(b.created_at).getMonth() === i);
        return { month, revenue: monthBookings.reduce((s, b) => s + (b.commission_amount || 0), 0), bookings: monthBookings.length };
      });
      setRevenueData(monthlyData);

      const cities = ['Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Hyderabad'];
      const cityData = cities.map(city => ({
        city,
        bookings: (allBookings || []).filter(b => b.venue?.toLowerCase().includes(city.toLowerCase())).length,
      }));
      setBookingsByCity(cityData);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <LoadingState />;

  const statCards = [
    { label: 'Total bookings', value: stats.totalBookings, color: '#1A1A1A', icon: '📅' },
    { label: 'Platform revenue', value: `₹${stats.totalRevenue.toLocaleString()}`, color: '#2E7D32', icon: '₹' },
    { label: 'Providers', value: stats.totalProviders, color: '#5C8CFF', icon: '🛠' },
    { label: 'Customers', value: stats.totalCustomers, color: '#E8A020', icon: '👥' },
    { label: 'Pending verifications', value: stats.pendingVerifications, color: '#C62828', icon: '⏳' },
    { label: "Today's bookings", value: stats.todayBookings, color: '#9C27B0', icon: '🆕' },
  ];

  return (
    <div>
      <PageHeader title="Overview" subtitle="Platform performance at a glance" />
      <div style={styles.statsGrid}>
        {statCards.map(card => (
          <div key={card.label} style={styles.statCard}>
            <div style={styles.statCardTop}>
              <span style={styles.statCardIcon}>{card.icon}</span>
              <span style={{ ...styles.statCardValue, color: card.color }}>{card.value}</span>
            </div>
            <p style={styles.statCardLabel}>{card.label}</p>
          </div>
        ))}
      </div>

      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Monthly revenue (₹)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EE" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
              <Line type="monotone" dataKey="revenue" stroke="#1A1A1A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Bookings by city</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={bookingsByCity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EE" />
              <XAxis dataKey="city" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="bookings" fill="#E8A020" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={styles.tableCard}>
        <h3 style={styles.chartTitle}>Recent bookings</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Customer', 'Event type', 'Date', 'Amount', 'Status'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recentBookings.map(b => (
              <tr key={b.id} style={styles.tr}>
                <td style={styles.td}>{b.users?.name || '—'}</td>
                <td style={styles.td}>{b.event_type}</td>
                <td style={styles.td}>{new Date(b.event_date).toLocaleDateString('en-IN')}</td>
                <td style={styles.td}>₹{b.total_amount?.toLocaleString()}</td>
                <td style={styles.td}><StatusBadge status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VerificationsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchRequests(); }, [activeTab]);

  async function fetchRequests() {
    try {
      setLoading(true);
      const { data } = await supabase
        .from('verification_requests')
        .select('*, providers(id, category, city, rating, total_reviews), users!user_id(name, email, phone)')
        .eq('status', activeTab)
        .order('submitted_at', { ascending: true });
      setRequests(data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(request, action) {
    if (!window.confirm(`${action === 'approved' ? 'Approve' : 'Reject'} ${request.users?.name}?`)) return;
    try {
      setProcessing(true);
      const { data: { session } } = await supabase.auth.getSession();
      await supabase.from('verification_requests').update({
        status: action,
        admin_notes: notes || null,
        reviewed_at: new Date().toISOString(),
        reviewed_by: session?.user.id,
      }).eq('id', request.id);

      if (action === 'approved') {
        await supabase.from('providers')
          .update({ is_verified: true, is_active: true })
          .eq('id', request.provider_id);
        await supabase.from('notifications').insert({
          user_id: request.user_id,
          title: 'Profile verified! ✓',
          body: 'Your provider profile has been verified.',
          data: { type: 'provider_verified' },
        });
      }
      setSelected(null);
      setNotes('');
      fetchRequests();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div>
      <PageHeader title="Verifications" subtitle="Review and approve provider applications" />
      <div style={styles.tabRow}>
        {['pending', 'approved', 'rejected'].map(tab => (
          <button key={tab} style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div style={styles.splitLayout}>
        <div style={styles.listPanel}>
          {loading ? <LoadingState /> : requests.length === 0 ? (
            <EmptyState icon="📋" message={`No ${activeTab} applications`} />
          ) : requests.map(req => (
            <div
              key={req.id}
              style={{ ...styles.listItem, ...(selected?.id === req.id ? styles.listItemActive : {}) }}
              onClick={() => { setSelected(req); setNotes(''); }}
            >
              <div style={styles.listItemAvatar}>{req.users?.name?.[0]}</div>
              <div style={styles.listItemInfo}>
                <p style={styles.listItemName}>{req.users?.name}</p>
                <p style={styles.listItemSub}>{req.business_name}</p>
                <p style={styles.listItemMeta}>{req.providers?.category} · {req.providers?.city}</p>
              </div>
              <p style={styles.listItemDate}>
                {new Date(req.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </p>
            </div>
          ))}
        </div>

        <div style={styles.detailPanel}>
          {!selected ? (
            <EmptyState icon="👈" message="Select an application to review" />
          ) : (
            <div>
              <div style={styles.detailHeader}>
                <div style={styles.detailAvatar}>{selected.users?.name?.[0]}</div>
                <div>
                  <h3 style={styles.detailName}>{selected.users?.name}</h3>
                  <p style={styles.detailEmail}>{selected.users?.email}</p>
                  <p style={styles.detailEmail}>{selected.users?.phone}</p>
                </div>
              </div>

              {[
                ['Business name', selected.business_name],
                ['Business type', selected.business_type],
                ['Experience', selected.years_experience],
                ['Service areas', selected.service_areas],
                ['Category', selected.providers?.category],
                ['City', selected.providers?.city],
                ['Rating', selected.providers?.rating ? `⭐ ${selected.providers.rating.toFixed(1)}` : 'No ratings'],
                ['Reviews', selected.providers?.total_reviews || 0],
                ['Submitted', new Date(selected.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })],
              ].map(([label, value]) => (
                <div key={label} style={styles.detailRow}>
                  <span style={styles.detailLabel}>{label}</span>
                  <span style={styles.detailValue}>{value || '—'}</span>
                </div>
              ))}

              {activeTab === 'pending' && (
                <>
                  <div style={styles.notesWrap}>
                    <label style={styles.notesLabel}>Admin notes (optional)</label>
                    <textarea
                      style={styles.notesInput}
                      placeholder="Add notes for the provider..."
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                    />
                  </div>
                  <div style={styles.actionBtns}>
                    <button
                      style={styles.rejectBtn}
                      onClick={() => handleAction(selected, 'rejected')}
                      disabled={processing}
                    >
                      ✗ Reject
                    </button>
                    <button
                      style={styles.approveBtn}
                      onClick={() => handleAction(selected, 'approved')}
                      disabled={processing}
                    >
                      {processing ? 'Processing...' : '✓ Approve & verify'}
                    </button>
                  </div>
                </>
              )}

              {selected.admin_notes && (
                <div style={styles.adminNotesBox}>
                  <p style={styles.adminNotesTitle}>Admin notes</p>
                  <p style={styles.adminNotesText}>{selected.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchBookings(); }, [filter]);

  async function fetchBookings() {
    try {
      setLoading(true);
      let query = supabase
        .from('bookings')
        .select('*, users!customer_id(name, email)')
        .order('created_at', { ascending: false })
        .limit(100);
      if (filter !== 'all') query = query.eq('status', filter);
      const { data } = await query;
      setBookings(data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  const filtered = bookings.filter(b =>
    b.users?.name?.toLowerCase().includes(search.toLowerCase()) ||
    b.event_type?.toLowerCase().includes(search.toLowerCase()) ||
    b.venue?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Bookings" subtitle="All platform bookings" />
      <div style={styles.filterRow}>
        <input style={styles.searchInput} placeholder="Search by customer, event, venue..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <select style={styles.select} value={filter} onChange={e => setFilter(e.target.value)}>
          {['all', 'pending', 'confirmed', 'completed', 'declined', 'cancelled'].map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {loading ? <LoadingState /> : (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Customer', 'Event', 'Date', 'Venue', 'Guests', 'Amount', 'Commission', 'Status'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id} style={styles.tr}>
                  <td style={styles.td}>{b.users?.name || '—'}</td>
                  <td style={styles.td}>{b.event_type}</td>
                  <td style={styles.td}>{new Date(b.event_date).toLocaleDateString('en-IN')}</td>
                  <td style={styles.td}>{b.venue}</td>
                  <td style={styles.td}>{b.guest_count}</td>
                  <td style={styles.td}>₹{b.total_amount?.toLocaleString()}</td>
                  <td style={styles.td}>₹{b.commission_amount?.toLocaleString()}</td>
                  <td style={styles.td}><StatusBadge status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={styles.tableFooter}>{filtered.length} bookings</p>
        </div>
      )}
    </div>
  );
}

// Minimal dispute visibility — a booking flagged via BookingsScreen.js's or
// ProviderERP.js's "Something's wrong" action. This is NOT a full
// resolution workflow (that's a follow-up); it's exactly what the task
// asked for: see what's going on (who raised it, when, the notes), and a
// "mark resolved" action that manually flips the booking to completed or
// cancelled once an admin has sorted it out outside the app (phone call,
// etc.). Split list/detail layout matches VerificationsTab above — same
// approve/reject-shaped precedent, just two different resolution buttons.
function DisputesTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('raised');
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDisputes(); }, [activeTab]);

  async function fetchDisputes() {
    try {
      setLoading(true);
      setSelected(null);
      const { data: disputeBookings } = await supabase
        .from('bookings')
        .select('*')
        .eq('dispute_status', activeTab)
        .order('dispute_raised_at', { ascending: false });

      if (!disputeBookings?.length) { setBookings([]); return; }

      // No joins — same manual-merge convention as every other tab here.
      const customerIds = [...new Set(disputeBookings.map(b => b.customer_id).filter(Boolean))];
      const providerIds = [...new Set(disputeBookings.map(b => b.provider_id).filter(Boolean))];
      const [{ data: customers }, { data: providers }] = await Promise.all([
        supabase.from('users').select('id, name, email, phone').in('id', customerIds),
        supabase.from('providers').select('id, user_id, category').in('id', providerIds),
      ]);
      const providerUserIds = [...new Set((providers || []).map(p => p.user_id).filter(Boolean))];
      const { data: providerUsers } = providerUserIds.length
        ? await supabase.from('users').select('id, name, phone').in('id', providerUserIds)
        : { data: [] };

      const merged = disputeBookings.map(b => {
        const provider = providers?.find(p => p.id === b.provider_id) || null;
        const providerUser = provider ? providerUsers?.find(u => u.id === provider.user_id) : null;
        return {
          ...b,
          customer: customers?.find(c => c.id === b.customer_id) || null,
          providerName: providerUser?.name || 'Provider',
          providerCategory: provider?.category,
        };
      });
      setBookings(merged);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  async function resolveDispute(booking, outcome) {
    const label = outcome === 'completed' ? 'mark completed' : 'mark cancelled';
    if (!window.confirm(`Resolve this dispute and ${label}? This cannot be undone from here.`)) return;
    try {
      setProcessing(true);
      const patch = {
        dispute_status: 'resolved',
        status: outcome,
        ...(outcome === 'completed' ? { completed_at: new Date().toISOString() } : {}),
      };
      const { error } = await supabase.from('bookings').update(patch).eq('id', booking.id);
      if (error) throw error;
      setSelected(null);
      fetchDisputes();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div>
      <PageHeader title="Disputes" subtitle="Bookings flagged by a host or provider" />
      <div style={styles.tabRow}>
        {['raised', 'resolved'].map(tab => (
          <button key={tab} style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab)}>
            {tab === 'raised' ? 'Open' : 'Resolved'}
          </button>
        ))}
      </div>

      <div style={styles.splitLayout}>
        <div style={styles.listPanel}>
          {loading ? <LoadingState /> : bookings.length === 0 ? (
            <EmptyState icon="✅" message={`No ${activeTab === 'raised' ? 'open' : 'resolved'} disputes`} />
          ) : bookings.map(b => (
            <div
              key={b.id}
              style={{ ...styles.listItem, ...(selected?.id === b.id ? styles.listItemActive : {}) }}
              onClick={() => setSelected(b)}
            >
              <div style={styles.listItemAvatar}>{b.customer?.name?.[0] || '?'}</div>
              <div style={styles.listItemInfo}>
                <p style={styles.listItemName}>{b.customer?.name || 'Customer'} vs {b.providerName}</p>
                <p style={styles.listItemSub}>{b.event_type} · raised by {b.dispute_raised_by}</p>
                <p style={styles.listItemMeta}>{b.venue}</p>
              </div>
              <p style={styles.listItemDate}>
                {b.dispute_raised_at ? new Date(b.dispute_raised_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
              </p>
            </div>
          ))}
        </div>

        <div style={styles.detailPanel}>
          {!selected ? (
            <EmptyState icon="👈" message="Select a dispute to review" />
          ) : (
            <div>
              <div style={styles.detailHeader}>
                <div style={styles.detailAvatar}>{selected.customer?.name?.[0] || '?'}</div>
                <div>
                  <h3 style={styles.detailName}>{selected.customer?.name || 'Customer'} vs {selected.providerName}</h3>
                  <p style={styles.detailEmail}>{selected.customer?.email}</p>
                  <p style={styles.detailEmail}>{selected.customer?.phone}</p>
                </div>
              </div>

              {[
                ['Event type', selected.event_type],
                ['Event date', new Date(selected.event_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })],
                ['Venue', selected.venue],
                ['Provider category', selected.providerCategory],
                ['Amount', `₹${selected.total_amount?.toLocaleString()}`],
                ['Raised by', selected.dispute_raised_by === 'host' ? 'Host (customer)' : 'Provider'],
                ['Raised at', selected.dispute_raised_at ? new Date(selected.dispute_raised_at).toLocaleString('en-IN') : '—'],
                ['Booking status', selected.status],
              ].map(([label, value]) => (
                <div key={label} style={styles.detailRow}>
                  <span style={styles.detailLabel}>{label}</span>
                  <span style={styles.detailValue}>{value || '—'}</span>
                </div>
              ))}

              <div style={styles.adminNotesBox}>
                <p style={styles.adminNotesTitle}>What went wrong (their words)</p>
                <p style={styles.adminNotesText}>{selected.dispute_notes || 'No notes provided.'}</p>
              </div>

              {activeTab === 'raised' && (
                <div style={styles.actionBtns}>
                  <button
                    style={styles.rejectBtn}
                    onClick={() => resolveDispute(selected, 'cancelled')}
                    disabled={processing}
                  >
                    Resolve → Cancel booking
                  </button>
                  <button
                    style={styles.approveBtn}
                    onClick={() => resolveDispute(selected, 'completed')}
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Resolve → Mark completed'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProvidersTab() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchProviders(); }, []);

  async function fetchProviders() {
    try {
      setLoading(true);
      const { data: providerData } = await supabase
        .from('providers')
        .select('*')
        .order('created_at', { ascending: false });
      if (!providerData?.length) { setProviders([]); return; }
      const { data: usersData } = await supabase
        .from('users')
        .select('id, name, email, phone')
        .in('id', providerData.map(p => p.user_id));
      setProviders(providerData.map(p => ({
        ...p,
        user: usersData?.find(u => u.id === p.user_id),
      })));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  async function toggleVerified(provider) {
    const newVal = !provider.is_verified;
    await supabase.from('providers').update({ is_verified: newVal }).eq('id', provider.id);
    setProviders(prev => prev.map(p => p.id === provider.id ? { ...p, is_verified: newVal } : p));
  }

  async function toggleActive(provider) {
    const newVal = !provider.is_active;
    await supabase.from('providers').update({ is_active: newVal }).eq('id', provider.id);
    setProviders(prev => prev.map(p => p.id === provider.id ? { ...p, is_active: newVal } : p));
  }

  const filtered = providers.filter(p =>
    p.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase()) ||
    p.city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Providers" subtitle={`${providers.length} registered providers`} />
      <div style={styles.filterRow}>
        <input style={styles.searchInput} placeholder="Search by name, category, city..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? <LoadingState /> : (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Name', 'Category', 'City', 'Rating', 'Reviews', 'Verified', 'Active', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div>
                      <p style={{ fontWeight: 600 }}>{p.user?.name || '—'}</p>
                      <p style={{ fontSize: 12, color: '#888' }}>{p.user?.email}</p>
                    </div>
                  </td>
                  <td style={styles.td}>{p.category || '—'}</td>
                  <td style={styles.td}>{p.city || '—'}</td>
                  <td style={styles.td}>⭐ {p.rating?.toFixed(1) || 'New'}</td>
                  <td style={styles.td}>{p.total_reviews || 0}</td>
                  <td style={styles.td}>
                    <ToggleBtn value={p.is_verified} onChange={() => toggleVerified(p)} />
                  </td>
                  <td style={styles.td}>
                    <ToggleBtn value={p.is_active} onChange={() => toggleActive(p)} />
                  </td>
                  <td style={styles.td}>
                    <button style={styles.actionBtn}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={styles.tableFooter}>{filtered.length} providers</p>
        </div>
      )}
    </div>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchCustomers(); }, []);

  async function fetchCustomers() {
    try {
      setLoading(true);
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('role', 'customer')
        .order('created_at', { ascending: false });
      setCustomers(data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${customers.length} registered customers`} />
      <div style={styles.filterRow}>
        <input style={styles.searchInput} placeholder="Search customers..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? <LoadingState /> : (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Name', 'Email', 'Phone', 'City', 'Joined'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} style={styles.tr}>
                  <td style={styles.td}>{c.name || '—'}</td>
                  <td style={styles.td}>{c.email}</td>
                  <td style={styles.td}>{c.phone || '—'}</td>
                  <td style={styles.td}>{c.city || '—'}</td>
                  <td style={styles.td}>
                    {new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={styles.tableFooter}>{filtered.length} customers</p>
        </div>
      )}
    </div>
  );
}

function RevenueTab() {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ total: 0, thisMonth: 0, lastMonth: 0, growth: 0 });
  const [byCategory, setByCategory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRevenue(); }, []);

  async function fetchRevenue() {
    try {
      setLoading(true);
      const { data: bookings } = await supabase
        .from('bookings')
        .select('commission_amount, total_amount, event_type, created_at')
        .eq('status', 'confirmed');

      const now = new Date();
      const thisMonth = (bookings || []).filter(b => {
        const d = new Date(b.created_at);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });
      const lastMonth = (bookings || []).filter(b => {
        const d = new Date(b.created_at);
        const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear();
      });

      const total = (bookings || []).reduce((s, b) => s + (b.commission_amount || 0), 0);
      const thisMonthTotal = thisMonth.reduce((s, b) => s + (b.commission_amount || 0), 0);
      const lastMonthTotal = lastMonth.reduce((s, b) => s + (b.commission_amount || 0), 0);
      const growth = lastMonthTotal > 0 ? Math.round(((thisMonthTotal - lastMonthTotal) / lastMonthTotal) * 100) : 0;
      setSummary({ total, thisMonth: thisMonthTotal, lastMonth: lastMonthTotal, growth });

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthlyData = months.map((month, i) => {
        const mb = (bookings || []).filter(b => new Date(b.created_at).getMonth() === i);
        return { month, commission: mb.reduce((s, b) => s + (b.commission_amount || 0), 0), gmv: mb.reduce((s, b) => s + (b.total_amount || 0), 0), count: mb.length };
      });
      setData(monthlyData);

      const categoryMap = {};
      (bookings || []).forEach(b => {
        categoryMap[b.event_type] = (categoryMap[b.event_type] || 0) + (b.commission_amount || 0);
      });
      setByCategory(Object.entries(categoryMap).map(([name, value]) => ({ name, value })));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <LoadingState />;

  return (
    <div>
      <PageHeader title="Revenue" subtitle="Platform earnings and GMV" />

      <div style={styles.statsGrid}>
        {[
          { label: 'Total platform revenue', value: `₹${summary.total.toLocaleString()}`, color: '#2E7D32' },
          { label: 'This month', value: `₹${summary.thisMonth.toLocaleString()}`, color: '#1A1A1A' },
          { label: 'Last month', value: `₹${summary.lastMonth.toLocaleString()}`, color: '#888' },
          { label: 'Month on month growth', value: `${summary.growth > 0 ? '+' : ''}${summary.growth}%`, color: summary.growth >= 0 ? '#2E7D32' : '#C62828' },
        ].map(card => (
          <div key={card.label} style={styles.statCard}>
            <span style={{ ...styles.statCardValue, color: card.color }}>{card.value}</span>
            <p style={styles.statCardLabel}>{card.label}</p>
          </div>
        ))}
      </div>

      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Monthly commission vs GMV</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EE" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
              <Bar dataKey="gmv" fill="#E8E8E4" radius={[4, 4, 0, 0]} name="GMV" />
              <Bar dataKey="commission" fill="#1A1A1A" radius={[4, 4, 0, 0]} name="Commission" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Revenue by event type</h3>
          {byCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={byCategory} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name }) => name}>
                  {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState icon="📊" message="No revenue data yet" />}
        </div>
      </div>

      <div style={styles.tableCard}>
        <h3 style={styles.chartTitle}>Monthly breakdown</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Month', 'Bookings', 'GMV', 'Commission', 'Avg per booking'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.filter(d => d.count > 0).map(d => (
              <tr key={d.month} style={styles.tr}>
                <td style={styles.td}>{d.month}</td>
                <td style={styles.td}>{d.count}</td>
                <td style={styles.td}>₹{d.gmv.toLocaleString()}</td>
                <td style={styles.td}>₹{d.commission.toLocaleString()}</td>
                <td style={styles.td}>₹{d.count > 0 ? Math.round(d.commission / d.count).toLocaleString() : 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending: { bg: '#FFF8F0', color: '#E8A020' },
    confirmed: { bg: '#E8F5E9', color: '#2E7D32' },
    completed: { bg: '#E8F5E9', color: '#2E7D32' },
    declined: { bg: '#FFEBEE', color: '#C62828' },
    cancelled: { bg: '#F5F5F5', color: '#888' },
    approved: { bg: '#E8F5E9', color: '#2E7D32' },
    rejected: { bg: '#FFEBEE', color: '#C62828' },
  };
  const c = config[status] || config.pending;
  return (
    <span style={{ backgroundColor: c.bg, color: c.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
      {status}
    </span>
  );
}

function ToggleBtn({ value, onChange }) {
  return (
    <button
      onClick={onChange}
      style={{ background: value ? '#1A1A1A' : '#E8E8E4', border: 'none', borderRadius: 12, padding: '4px 12px', color: value ? '#FFF' : '#888', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
    >
      {value ? 'Yes' : 'No'}
    </button>
  );
}

function PageHeader({ title, subtitle }) {
  return (
    <div style={styles.pageHeader}>
      <h2 style={styles.pageTitle}>{title}</h2>
      <p style={styles.pageSub}>{subtitle}</p>
    </div>
  );
}

function LoadingState() {
  return <div style={styles.center}><div style={styles.spinner} /></div>;
}

function EmptyState({ icon, message }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: '#888' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <p style={{ fontSize: 14 }}>{message}</p>
    </div>
  );
}

const styles = {
  app: { display: 'flex', minHeight: '100vh' },
  main: { flex: 1, padding: 32, overflowY: 'auto', maxWidth: 1200 },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  spinner: { width: 32, height: 32, border: '3px solid #E8E8E4', borderTopColor: '#1A1A1A', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loginWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#F8F8F6' },
  loginCard: { background: '#FFF', borderRadius: 20, padding: 40, width: 380, boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  loginTitle: { fontSize: 24, fontWeight: 700, marginBottom: 6, textAlign: 'center' },
  loginSub: { color: '#888', textAlign: 'center', marginBottom: 28, fontSize: 14 },
  loginInput: { display: 'block', width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #E8E8E4', marginBottom: 12, fontSize: 14, outline: 'none', background: '#F8F8F6' },
  loginBtn: { display: 'block', width: '100%', padding: '14px', background: '#1A1A1A', color: '#FFF', borderRadius: 12, fontSize: 15, fontWeight: 700, marginTop: 8 },
  loginError: { color: '#C62828', fontSize: 13, marginBottom: 10, textAlign: 'center' },
  sidebar: { width: 220, background: '#1A1A1A', display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'sticky', top: 0 },
  sidebarTop: { padding: '28px 20px 16px' },
  sidebarLogoRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  sidebarLogoImg: { width: 32, height: 32 },
  sidebarTitle: { fontSize: 20, fontWeight: 700, color: '#FFF', margin: 0 },
  sidebarSub: { fontSize: 12, color: '#888' },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 20px', background: 'transparent', color: '#888', fontSize: 14, textAlign: 'left', borderRadius: 0 },
  navItemActive: { background: 'rgba(255,255,255,0.08)', color: '#FFF' },
  navIcon: { fontSize: 16, width: 20 },
  sidebarBottom: { marginTop: 'auto', padding: 20, borderTop: '1px solid rgba(255,255,255,0.08)' },
  sidebarEmail: { fontSize: 12, color: '#888', marginBottom: 10, wordBreak: 'break-all' },
  logoutBtn: { background: 'rgba(255,255,255,0.08)', color: '#888', padding: '8px 14px', borderRadius: 8, fontSize: 13, width: '100%' },
  pageHeader: { marginBottom: 24 },
  pageTitle: { fontSize: 26, fontWeight: 700, marginBottom: 4 },
  pageSub: { color: '#888', fontSize: 14 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 },
  statCard: { background: '#FFF', borderRadius: 14, padding: 16, border: '1px solid #E8E8E4' },
  statCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  statCardIcon: { fontSize: 20 },
  statCardValue: { fontSize: 24, fontWeight: 700 },
  statCardLabel: { fontSize: 12, color: '#888' },
  chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 },
  chartCard: { background: '#FFF', borderRadius: 14, padding: 20, border: '1px solid #E8E8E4' },
  chartTitle: { fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#1A1A1A' },
  tableCard: { background: '#FFF', borderRadius: 14, padding: 20, border: '1px solid #E8E8E4', marginBottom: 24, overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#888', borderBottom: '1px solid #E8E8E4', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #F5F5F0' },
  td: { padding: '12px 12px', fontSize: 13, verticalAlign: 'middle' },
  tableFooter: { fontSize: 12, color: '#888', marginTop: 12, textAlign: 'right' },
  tabRow: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { padding: '8px 18px', borderRadius: 20, background: '#F5F5F0', color: '#888', fontSize: 13, fontWeight: 500, border: '1px solid #E8E8E4' },
  tabActive: { background: '#1A1A1A', color: '#FFF', borderColor: '#1A1A1A' },
  filterRow: { display: 'flex', gap: 12, marginBottom: 16 },
  searchInput: { flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #E8E8E4', fontSize: 14, outline: 'none', background: '#F8F8F6' },
  select: { padding: '10px 14px', borderRadius: 10, border: '1px solid #E8E8E4', fontSize: 14, outline: 'none', background: '#F8F8F6' },
  splitLayout: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16 },
  listPanel: { background: '#FFF', borderRadius: 14, border: '1px solid #E8E8E4', overflow: 'hidden', maxHeight: 600, overflowY: 'auto' },
  listItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #F5F5F0' },
  listItemActive: { background: '#F8F8F6' },
  listItemAvatar: { width: 42, height: 42, borderRadius: 21, background: '#E8A020', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF', fontWeight: 700, fontSize: 16, flexShrink: 0 },
  listItemInfo: { flex: 1 },
  listItemName: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
  listItemSub: { fontSize: 12, color: '#888', marginBottom: 2 },
  listItemMeta: { fontSize: 11, color: '#BBB' },
  listItemDate: { fontSize: 11, color: '#BBB', flexShrink: 0 },
  detailPanel: { background: '#FFF', borderRadius: 14, border: '1px solid #E8E8E4', padding: 20, maxHeight: 600, overflowY: 'auto' },
  detailHeader: { display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #E8E8E4' },
  detailAvatar: { width: 52, height: 52, borderRadius: 26, background: '#E8A020', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF', fontWeight: 700, fontSize: 20 },
  detailName: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  detailEmail: { fontSize: 13, color: '#888' },
  detailRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F5F5F0', fontSize: 13 },
  detailLabel: { color: '#888' },
  detailValue: { fontWeight: 500 },
  notesWrap: { marginTop: 20 },
  notesLabel: { fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 },
  notesInput: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #E8E8E4', fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 80, background: '#F8F8F6' },
  actionBtns: { display: 'flex', gap: 10, marginTop: 16 },
  rejectBtn: { flex: 1, padding: '12px', borderRadius: 10, border: '2px solid #C62828', color: '#C62828', fontWeight: 700, fontSize: 14, background: 'transparent' },
  approveBtn: { flex: 2, padding: '12px', borderRadius: 10, background: '#2E7D32', color: '#FFF', fontWeight: 700, fontSize: 14 },
  adminNotesBox: { marginTop: 16, padding: 14, background: '#FFEBEE', borderRadius: 10, border: '1px solid #FFCDD2' },
  adminNotesTitle: { fontSize: 12, fontWeight: 600, color: '#C62828', marginBottom: 6 },
  adminNotesText: { fontSize: 13, color: '#C62828' },
  actionBtn: { padding: '6px 14px', borderRadius: 8, background: '#F5F5F0', color: '#1A1A1A', fontSize: 12, fontWeight: 500 },
};